/**
 * Hero Image with Rotating Status Badge
 *
 * Displays a "peace of mind" lifestyle image (person relaxing with coffee)
 * with a floating notification-style badge that cycles through scheduling
 * status updates. Conveys the core value prop visually: you relax, we deliver.
 *
 * Raster: WebP variants under `public/illustrations/hero-peace-of-mind-*.webp`
 * (520 / 1040 widths for the ~520 CSS px hero) with PNG fallback. Intrinsic
 * size 1536×1024 reserves aspect ratio to avoid CLS (PBI #1135).
 * The badge rotates every ~3s with a fade/slide transition, showing one
 * occasion at a time (e.g., "Mom's Birthday — Scheduled").
 *
 * Uses CSS-only animation for the badge transition and a JS timer to
 * cycle through occasions.
 */

import { useState, useEffect, useCallback } from 'react';
import { Box, Group, Stack, Text, rem } from '@mantine/core';
import { IconCheck } from '@tabler/icons-react';
import { semanticColors } from '../../config/theme';

/** Intrinsic pixel size of the source illustration (for CLS-safe layout). */
const HERO_ILLUSTRATION_WIDTH = 1536;
const HERO_ILLUSTRATION_HEIGHT = 1024;

const HERO_ILLUSTRATION_PNG = '/illustrations/hero-peace-of-mind.png';
const HERO_ILLUSTRATION_WEBP_SRCSET =
  '/illustrations/hero-peace-of-mind-520.webp 520w, /illustrations/hero-peace-of-mind-1040.webp 1040w';
/** `sizes` matches the hero's maxWidth 520 CSS px. */
const HERO_ILLUSTRATION_SIZES = '(max-width: 576px) 100vw, 520px';

/** Duration (ms) each badge is fully visible before transitioning */
const BADGE_DISPLAY_MS = 3000;

/** Duration (ms) of the fade transition */
const BADGE_TRANSITION_MS = 400;

type BadgePhase = 'entering' | 'visible' | 'exiting';

interface StatusOccasion {
  label: string;
  status: string;
}

/** Occasions shown in the rotating badge overlay — e-cards, mailed cards, and flowers */
const OCCASIONS: StatusOccasion[] = [
  { label: 'Mom\u2019s Birthday E-Card', status: 'Scheduled' },
  { label: 'Mother\u2019s Day Flowers', status: 'Scheduled' },
  { label: 'Anniversary Mailed Card', status: 'Sent' },
  { label: 'Father\u2019s Day E-Card for Dad', status: 'Scheduled' },
];

/**
 * Renders the hero image with a floating rotating status badge.
 * The image fills the container with rounded corners; the badge is
 * positioned at the bottom-right as a notification-style overlay.
 */
export const HeroImageWithBadge = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [phase, setPhase] = useState<BadgePhase>('entering');

  const advanceBadge = useCallback(() => {
    setPhase('exiting');
    setTimeout(() => {
      setCurrentIndex((prev) => (prev + 1) % OCCASIONS.length);
      setPhase('entering');
    }, BADGE_TRANSITION_MS);
  }, []);

  useEffect(() => {
    if (phase === 'entering') {
      const timer = setTimeout(() => setPhase('visible'), BADGE_TRANSITION_MS);
      return () => clearTimeout(timer);
    }
    if (phase === 'visible') {
      const timer = setTimeout(advanceBadge, BADGE_DISPLAY_MS);
      return () => clearTimeout(timer);
    }
  }, [phase, advanceBadge]);

  const current = OCCASIONS[currentIndex];

  const getBadgeOpacity = () => {
    if (phase === 'entering') return 0;
    if (phase === 'visible') return 1;
    return 0;
  };

  const getBadgeTranslate = () => {
    if (phase === 'entering') return 'translateY(8px)';
    if (phase === 'visible') return 'translateY(0)';
    return 'translateY(-8px)';
  };

  return (
    <Box
      style={{
        position: 'relative',
        borderRadius: 'var(--mantine-radius-lg)',
        overflow: 'hidden',
        width: '100%',
        maxWidth: 520,
        margin: '0 auto',

        // Subtle indigo-tinted border + soft shadow to lift the hero image
        // off the warm page background and give it a polished card feel.
        border: '1px solid var(--mantine-color-indigo-1)',
        boxShadow:
          '0 10px 30px rgba(49, 46, 129, 0.12), 0 4px 10px rgba(49, 46, 129, 0.06)',
      }}
    >
      {/* Background image — noCoverOnCardImages.test.ts allowlist: marketing hero illustration, not template artwork */}
      <picture>
        <source
          type="image/webp"
          srcSet={HERO_ILLUSTRATION_WEBP_SRCSET}
          sizes={HERO_ILLUSTRATION_SIZES}
        />
        <Box
          component="img"
          src={HERO_ILLUSTRATION_PNG}
          width={HERO_ILLUSTRATION_WIDTH}
          height={HERO_ILLUSTRATION_HEIGHT}
          alt="A relaxed person enjoying coffee — peace of mind with Scheduled Smiles"
          fetchPriority="high"
          decoding="async"
          style={{
            width: '100%',
            height: 'auto',
            display: 'block',
            objectFit: 'cover',
            aspectRatio: `${HERO_ILLUSTRATION_WIDTH} / ${HERO_ILLUSTRATION_HEIGHT}`,
            minHeight: 300,
          }}
        />
      </picture>

      {/* Floating status badge — border matches the green check mark (teal) */}
      <Box
        style={{
          position: 'absolute',
          bottom: rem(20),
          right: rem(20),
          background: 'white',
          borderRadius: 'var(--mantine-radius-md)',
          padding: `${rem(10)} ${rem(16)}`,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)',
          border: `2px solid var(--mantine-color-teal-6)`,
          opacity: getBadgeOpacity(),
          transform: getBadgeTranslate(),
          transition: `opacity ${BADGE_TRANSITION_MS}ms ease, transform ${BADGE_TRANSITION_MS}ms ease`,
          minWidth: 200,
        }}
      >
        <Stack gap={2}>
          <Text
            size="xs"
            fw={600}
            tt="uppercase"
            c={semanticColors.text.secondary}
            style={{ letterSpacing: 0.5 }}
          >
            Status Update
          </Text>
          <Group gap="xs" wrap="nowrap">
            <Text size="sm" fw={600} c={semanticColors.text.heading}>
              {current.label}
            </Text>
          </Group>
          <Group gap={4} wrap="nowrap">
            <IconCheck size={14} color="var(--mantine-color-teal-6)" />
            <Text size="xs" fw={600} c="teal.7">
              {current.status}
            </Text>
          </Group>
        </Stack>
      </Box>
    </Box>
  );
};
